import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cn } from '@/lib/utils'
import { Spinner } from './primitives'

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger'
type Size = 'sm' | 'md' | 'lg'

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-primary text-white hover:bg-primary-dark active:bg-primary-dark disabled:bg-primary/50',
  secondary:
    'bg-white text-gray-900 border border-divider hover:bg-background active:bg-border disabled:opacity-50',
  ghost: 'bg-transparent text-primary hover:bg-primary/8 active:bg-primary/12 disabled:opacity-50',
  danger: 'bg-danger text-white hover:brightness-95 active:brightness-90 disabled:opacity-50',
}

// Минимальная высота 44px — рекомендованный размер цели нажатия.
// Мобильное приложение полагалось на нативные Pressable, в вебе это надо задавать.
const SIZES: Record<Size, string> = {
  sm: 'h-11 px-4 text-sm gap-1.5',
  md: 'h-12 px-5 text-base gap-2',
  lg: 'h-14 px-6 text-lg gap-2',
}

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant
  size?: Size
  loading?: boolean
  icon?: ReactNode
  fullWidth?: boolean
  asChild?: boolean
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    loading = false,
    icon,
    fullWidth = false,
    asChild = false,
    className,
    children,
    disabled,
    ...props
  },
  ref,
) {
  const classes = cn(
    'inline-flex items-center justify-center rounded-card font-semibold',
    'transition-colors select-none disabled:cursor-not-allowed',
    VARIANTS[variant],
    SIZES[size],
    fullWidth && 'w-full',
    className,
  )

  // При asChild стили переносятся на переданный элемент (обычно ссылку).
  // Slot принимает ровно один дочерний узел, поэтому иконку и спиннер
  // здесь подставлять нельзя — вызывающий код кладёт их внутрь сам.
  if (asChild) {
    return (
      <Slot className={classes} {...props}>
        {children}
      </Slot>
    )
  }

  return (
    <button
      ref={ref}
      disabled={disabled || loading}
      className={classes}
      {...props}
    >
      {loading ? <Spinner size={size === 'sm' ? 16 : 20} /> : icon}
      {children}
    </button>
  )
})
